import JSZip from 'jszip';
import { Product, Category, PageSEO, SitePagesContent, StoriesSettings, StoryItem } from '../types';
import { VALID_IMAGE_CATEGORY_FOLDERS, normalizeCategoryFolder, ImageCategoryFolder } from './categoryFolders';
import { executeSmartMerge, SmartMergeResult } from './smartMerge';

export interface BackupProgressCallback {
  (progress: {
    stage: 'gathering' | 'downloading_images' | 'compressing' | 'restoring_images' | 'merging' | 'complete';
    message: string;
    percent: number;
    current: number;
    total: number;
  }): void;
}

export interface StoreBackupZipData {
  version: string;
  timestamp: string;
  exportedAt: string;
  storeName: string;
  productCount: number;
  categoriesCount: number;
  categoryFolders: readonly string[];
  products: Product[];
  categories?: Category[];
  savedCustomTags?: any[];
  pagesContent?: SitePagesContent;
  storiesSettings?: StoriesSettings;
  pagesSEO?: Record<string, PageSEO> | PageSEO[];
}

/**
 * Creates and downloads a self-contained ZIP backup of all products along with their
 * image files neatly organized into the 11 category folders:
 * canvas, general, hats, hoodies, keychains, mugs, phone-cases, photo-frames, stickers, tshirts, wood-crafts.
 */
export async function createAndDownloadStoreZipBackup({
  products,
  categories,
  savedCustomTags,
  pagesContent,
  storiesSettings,
  pagesSEO,
  onProgress
}: {
  products: Product[];
  categories?: Category[];
  savedCustomTags?: any[];
  pagesContent?: SitePagesContent;
  storiesSettings?: StoriesSettings;
  pagesSEO?: Record<string, PageSEO> | PageSEO[];
  onProgress?: BackupProgressCallback;
}): Promise<void> {
  const zip = new JSZip();
  const rootFolderName = `coveristic-backup-${new Date().toISOString().slice(0, 10)}`;
  const root = zip.folder(rootFolderName) || zip;

  // 1. Create the 11 strictly approved category folders + stories folder
  VALID_IMAGE_CATEGORY_FOLDERS.forEach((folder) => {
    root.folder(`images/${folder}`);
  });
  root.folder('images/stories');

  const totalProducts = products.length;
  onProgress?.({
    stage: 'gathering',
    message: `Preparing backup for ${totalProducts} products...`,
    percent: 5,
    current: 0,
    total: totalProducts
  });

  const sanitizedProducts: Product[] = [];
  const processedImages = new Set<string>();

  // 2. Process all products & bundle images into category folders
  for (let i = 0; i < products.length; i++) {
    const p = products[i];
    const cat = normalizeCategoryFolder(p.category);
    const prodCopy: Product = { ...p, category: cat as any };

    const percent = Math.min(85, Math.round(10 + (i / totalProducts) * 75));
    onProgress?.({
      stage: 'downloading_images',
      message: `Archiving [${i + 1}/${totalProducts}]: ${p.title} (${cat})...`,
      percent,
      current: i + 1,
      total: totalProducts
    });

    // Handle primary image
    if (p.image && typeof p.image === 'string' && !p.image.startsWith('data:image/svg+xml')) {
      const imgFileName = await archiveImageToZip(root, p.image, cat, p.slug || p.id, 'main', processedImages);
      if (imgFileName) {
        prodCopy.image = `/images/${cat}/${imgFileName}`;
      }
    }

    // Handle additional images if any
    if (Array.isArray(p.additionalImages) && p.additionalImages.length > 0) {
      const newAdditional: string[] = [];
      for (let j = 0; j < p.additionalImages.length; j++) {
        const addlUrl = p.additionalImages[j];
        if (addlUrl && typeof addlUrl === 'string' && !addlUrl.startsWith('data:image/svg+xml')) {
          const addlFileName = await archiveImageToZip(root, addlUrl, cat, p.slug || p.id, `extra_${j + 1}`, processedImages);
          if (addlFileName) {
            newAdditional.push(`/images/${cat}/${addlFileName}`);
          } else {
            newAdditional.push(addlUrl);
          }
        }
      }
      prodCopy.additionalImages = newAdditional;
    }

    sanitizedProducts.push(prodCopy);
  }

  // 3. Process & Archive Coveristic Stories Images
  let sanitizedStories: StoriesSettings | undefined = storiesSettings;
  if (storiesSettings && Array.isArray(storiesSettings.items) && storiesSettings.items.length > 0) {
    const sanitizedStoryItems: StoryItem[] = [];

    for (let sIdx = 0; sIdx < storiesSettings.items.length; sIdx++) {
      const story = storiesSettings.items[sIdx];
      const storyCopy: StoryItem = { ...story };
      const storyIdent = story.id || `story_${sIdx + 1}`;

      // Archive main story image
      if (story.image && typeof story.image === 'string' && !story.image.startsWith('data:image/svg+xml')) {
        const archivedName = await archiveImageToZip(root, story.image, 'stories', storyIdent, 'cover', processedImages);
        if (archivedName) {
          storyCopy.image = `/images/stories/${archivedName}`;
        }
      }

      // Archive all slide images in this story
      if (Array.isArray(story.images) && story.images.length > 0) {
        const archivedSlideUrls: string[] = [];
        for (let imgIdx = 0; imgIdx < story.images.length; imgIdx++) {
          const slideUrl = story.images[imgIdx];
          if (slideUrl && typeof slideUrl === 'string' && !slideUrl.startsWith('data:image/svg+xml')) {
            const archivedName = await archiveImageToZip(root, slideUrl, 'stories', storyIdent, `slide_${imgIdx + 1}`, processedImages);
            if (archivedName) {
              archivedSlideUrls.push(`/images/stories/${archivedName}`);
            } else {
              archivedSlideUrls.push(slideUrl);
            }
          } else {
            archivedSlideUrls.push(slideUrl);
          }
        }
        storyCopy.images = archivedSlideUrls;
      }

      sanitizedStoryItems.push(storyCopy);
    }

    sanitizedStories = {
      ...storiesSettings,
      items: sanitizedStoryItems
    };
  }

  // 4. Write data JSONs
  const backupManifest: StoreBackupZipData = {
    version: '2.0',
    timestamp: new Date().toISOString(),
    exportedAt: new Date().toLocaleString(),
    storeName: 'Coveristic',
    productCount: sanitizedProducts.length,
    categoriesCount: categories ? categories.length : 0,
    categoryFolders: VALID_IMAGE_CATEGORY_FOLDERS,
    products: sanitizedProducts,
    categories,
    savedCustomTags,
    pagesContent,
    storiesSettings: sanitizedStories,
    pagesSEO
  };

  root.file('manifest.json', JSON.stringify(backupManifest, null, 2));
  root.file('products.json', JSON.stringify(sanitizedProducts, null, 2));
  if (categories) {
    root.file('categories.json', JSON.stringify(categories, null, 2));
  }
  if (Array.isArray(savedCustomTags) && savedCustomTags.length > 0) {
    root.file('tags.json', JSON.stringify(savedCustomTags, null, 2));
  }
  if (sanitizedStories) {
    root.file('stories.json', JSON.stringify(sanitizedStories, null, 2));
  }
  if (pagesContent) {
    root.file('pages.json', JSON.stringify(pagesContent, null, 2));
  }
  if (pagesSEO) {
    root.file('seo.json', JSON.stringify(pagesSEO, null, 2));
  }

  // Include blog articles and media in backup
  if (typeof window !== 'undefined') {
    try {
      const savedBlog = localStorage.getItem('coveristic_blog_articles_v2');
      if (savedBlog) {
        root.file('blog_articles.json', savedBlog);
      }
    } catch {}
  }

  // 5. Generate and trigger download
  onProgress?.({
    stage: 'compressing',
    message: 'Compressing archive with images...',
    percent: 90,
    current: totalProducts,
    total: totalProducts
  });

  const zipBlob = await zip.generateAsync(
    {
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    },
    (metadata) => {
      onProgress?.({
        stage: 'compressing',
        message: `Compressing package: ${Math.round(metadata.percent)}%`,
        percent: 90 + Math.round((metadata.percent / 100) * 8),
        current: totalProducts,
        total: totalProducts
      });
    }
  );

  const dateStamp = new Date().toISOString().slice(0, 10);
  const downloadFilename = `coveristic-products-backup-${dateStamp}.zip`;

  const downloadUrl = URL.createObjectURL(zipBlob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = downloadFilename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(downloadUrl);

  onProgress?.({
    stage: 'complete',
    message: `Backup complete! Downloaded ${downloadFilename}`,
    percent: 100,
    current: totalProducts,
    total: totalProducts
  });
}

/**
 * Helper to fetch or convert an image and write it to root.folder(`images/${cat}`)
 */
async function archiveImageToZip(
  root: JSZip,
  sourceUrl: string,
  category: ImageCategoryFolder,
  identifier: string,
  suffix: string,
  processedImages: Set<string>
): Promise<string | null> {
  try {
    let cleanExt = 'webp';
    let baseName = `${identifier}_${suffix}`;
    let fileBuffer: ArrayBuffer | Uint8Array | null = null;

    if (sourceUrl.startsWith('data:image/')) {
      // Base64 Data URL
      const extMatch = sourceUrl.match(/^data:image\/([a-zA-Z0-9_+]+);base64,/);
      if (extMatch && extMatch[1]) {
        cleanExt = extMatch[1].toLowerCase() === 'jpeg' ? 'jpg' : extMatch[1].toLowerCase();
      }
      const base64Data = sourceUrl.split(',')[1];
      if (!base64Data) return null;

      const binaryStr = atob(base64Data);
      const len = binaryStr.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      fileBuffer = bytes;
    } else {
      // URL (relative or absolute)
      const urlClean = sourceUrl.split('?')[0].split('#')[0];
      const parts = urlClean.split('/');
      const lastPart = parts[parts.length - 1];
      if (lastPart && lastPart.includes('.')) {
        const lastDot = lastPart.lastIndexOf('.');
        baseName = lastPart.substring(0, lastDot).replace(/[^a-zA-Z0-9_-]/g, '_');
        cleanExt = lastPart.substring(lastDot + 1).toLowerCase();
      }

      // Fetch binary image
      const fetchUrl = sourceUrl.startsWith('http') || sourceUrl.startsWith('/') 
        ? sourceUrl 
        : `/${sourceUrl}`;
      const response = await fetch(fetchUrl, { cache: 'no-cache' });
      if (!response.ok) {
        return null;
      }
      fileBuffer = await response.arrayBuffer();
    }

    if (!fileBuffer) return null;

    const fileName = `${baseName}.${cleanExt}`;
    const zipPath = `images/${category}/${fileName}`;

    if (!processedImages.has(zipPath)) {
      root.file(zipPath, fileBuffer);
      processedImages.add(zipPath);
    }

    return fileName;
  } catch (err) {
    // If fetching fails, skip image gracefully without crashing backup
    return null;
  }
}

/**
 * Restores or parses products from either a .zip backup or a raw .json backup
 */
export async function parseAndRestoreStoreBackup({
  file,
  currentProducts,
  mode = 'merge',
  onProgress,
  untrashAll = true
}: {
  file: File;
  currentProducts: Product[];
  mode?: 'merge' | 'replace';
  onProgress?: BackupProgressCallback;
  untrashAll?: boolean;
}): Promise<{
  success: boolean;
  message: string;
  mergedProducts: Product[];
  categories?: Category[];
  savedCustomTags?: any[];
  pagesContent?: any;
  storiesSettings?: StoriesSettings;
  pagesSEO?: any[];
  addedCount: number;
  updatedCount: number;
  imagesRestored: number;
}> {
  const isZip = file.name.endsWith('.zip') || file.type.includes('zip') || file.type.includes('octet-stream');

  try {
    let rawProducts: Product[] = [];
    let categories: Category[] | undefined;
    let savedCustomTags: any[] | undefined;
    let pagesContent: any | undefined;
    let storiesSettings: StoriesSettings | undefined;
    let pagesSEO: any[] | undefined;
    let imagesRestored = 0;

    if (isZip) {
      onProgress?.({
        stage: 'gathering',
        message: 'Reading backup ZIP archive...',
        percent: 10,
        current: 0,
        total: 100
      });

      const zip = await JSZip.loadAsync(file);

      // Locate products.json inside the zip
      let productsJsonFile = zip.file('products.json');
      if (!productsJsonFile) {
        // Check subfolder
        const match = Object.keys(zip.files).find(k => k.endsWith('products.json') && !zip.files[k].dir);
        if (match) {
          productsJsonFile = zip.file(match);
        }
      }

      if (!productsJsonFile) {
        // Fallback: check manifest.json
        const manifestFile = zip.file('manifest.json') || Object.values(zip.files).find(f => f.name.endsWith('manifest.json') && !f.dir);
        if (manifestFile) {
          const manifestContent = await manifestFile.async('text');
          const parsedManifest = JSON.parse(manifestContent);
          if (Array.isArray(parsedManifest.products)) {
            rawProducts = parsedManifest.products;
          }
          if (Array.isArray(parsedManifest.categories)) {
            categories = parsedManifest.categories;
          }
          if (Array.isArray(parsedManifest.savedCustomTags)) {
            savedCustomTags = parsedManifest.savedCustomTags;
          }
          if (parsedManifest.storiesSettings && typeof parsedManifest.storiesSettings === 'object') {
            storiesSettings = parsedManifest.storiesSettings;
          }
        }
      } else {
        const content = await productsJsonFile.async('text');
        rawProducts = JSON.parse(content);
      }

      // Check stories.json
      const storiesFile = zip.file('stories.json') || Object.values(zip.files).find(f => f.name.endsWith('stories.json') && !f.dir);
      if (storiesFile) {
        try {
          const storiesContent = await storiesFile.async('text');
          storiesSettings = JSON.parse(storiesContent);
        } catch {
          // Ignore stories parse errors
        }
      }

      // Check categories.json
      const catFile = zip.file('categories.json') || Object.values(zip.files).find(f => f.name.endsWith('categories.json') && !f.dir);
      if (catFile) {
        try {
          const catContent = await catFile.async('text');
          categories = JSON.parse(catContent);
        } catch {
          // Ignore category parse errors
        }
      }

      // Check tags.json
      const tagsFile = zip.file('tags.json') || Object.values(zip.files).find(f => f.name.endsWith('tags.json') && !f.dir);
      if (tagsFile) {
        try {
          const tagsContent = await tagsFile.async('text');
          savedCustomTags = JSON.parse(tagsContent);
        } catch {
          // Ignore tag parse errors
        }
      }

      // Check blog_articles.json
      const blogFile = zip.file('blog_articles.json') || Object.values(zip.files).find(f => f.name.endsWith('blog_articles.json') && !f.dir);
      if (blogFile) {
        try {
          const blogContent = await blogFile.async('text');
          if (blogContent && typeof window !== 'undefined') {
            localStorage.setItem('coveristic_blog_articles_v2', blogContent);
          }
        } catch {
          // Ignore blog parse errors
        }
      }


      // Extract & upload images to server /api/upload.php if available
      const imageEntries = Object.entries(zip.files).filter(([name, zipEntry]) => {
        return !zipEntry.dir && (
          name.includes('images/') ||
          name.endsWith('.webp') ||
          name.endsWith('.jpg') ||
          name.endsWith('.png') ||
          name.endsWith('.jpeg')
        );
      });

      const totalImages = imageEntries.length;
      if (totalImages > 0) {
        for (let i = 0; i < totalImages; i++) {
          const [filePath, zipEntry] = imageEntries[i];
          const pathParts = filePath.split('/');
          const filename = pathParts.pop() || `img_${Date.now()}.webp`;
          const rawCategory = pathParts[pathParts.length - 1] || 'general';
          const category = normalizeCategoryFolder(rawCategory);

          onProgress?.({
            stage: 'restoring_images',
            message: `Restoring image [${i + 1}/${totalImages}]: ${category}/${filename}...`,
            percent: 20 + Math.round(((i + 1) / totalImages) * 60),
            current: i + 1,
            total: totalImages
          });

          try {
            const base64Data = await zipEntry.async('base64');
            const mime = filename.endsWith('.png') ? 'image/png' : filename.endsWith('.jpg') || filename.endsWith('.jpeg') ? 'image/jpeg' : 'image/webp';
            const dataUrl = `data:${mime};base64,${base64Data}`;

            // Attempt to upload directly to hosting server /api/upload.php or Vite dev middleware
            await fetch('/api/upload.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                category,
                filename,
                image: dataUrl
              })
            }).catch(() => null);

            imagesRestored++;
          } catch {
            // Continue if an image fails
          }
        }
      }
    } else {
      // Raw JSON File
      onProgress?.({
        stage: 'gathering',
        message: 'Reading backup JSON file...',
        percent: 20,
        current: 0,
        total: 100
      });

      const jsonText = await file.text();
      const parsed = JSON.parse(jsonText);

      if (Array.isArray(parsed)) {
        rawProducts = parsed;
      } else if (parsed && Array.isArray(parsed.products)) {
        rawProducts = parsed.products;
        if (Array.isArray(parsed.categories)) {
          categories = parsed.categories;
        }
        if (Array.isArray(parsed.savedCustomTags)) {
          savedCustomTags = parsed.savedCustomTags;
        }
        if (parsed.storiesSettings && typeof parsed.storiesSettings === 'object') {
          storiesSettings = parsed.storiesSettings;
        }
        if (parsed.pagesContent && typeof parsed.pagesContent === 'object') {
          pagesContent = parsed.pagesContent;
        }
        if (Array.isArray(parsed.pagesSEO)) {
          pagesSEO = parsed.pagesSEO;
        }
      } else {
        return {
          success: false,
          message: 'Invalid backup file: no products array detected',
          mergedProducts: currentProducts,
          addedCount: 0,
          updatedCount: 0,
          imagesRestored: 0
        };
      }
    }

    if (!Array.isArray(rawProducts) || rawProducts.length === 0) {
      return {
        success: false,
        message: 'Backup archive contains 0 valid products',
        mergedProducts: currentProducts,
        addedCount: 0,
        updatedCount: 0,
        imagesRestored: 0
      };
    }

    // If untrashAll is requested (default true), un-trash any items in the backup so they appear active
    if (untrashAll) {
      rawProducts = rawProducts.map(p => ({
        ...p,
        inRecycleBin: false,
        trashedAt: undefined
      }));
    }

    onProgress?.({
      stage: 'merging',
      message: mode === 'merge' ? 'Applying Smart Merge...' : 'Replacing product catalog...',
      percent: 90,
      current: rawProducts.length,
      total: rawProducts.length
    });

    if (mode === 'replace') {
      const sanitized = rawProducts.map((p, idx) => ({
        ...p,
        id: p.id || `prod-${Date.now()}-${idx}`,
        category: normalizeCategoryFolder(p.category) as any,
        inRecycleBin: untrashAll ? false : Boolean(p.inRecycleBin),
        trashedAt: untrashAll ? undefined : p.trashedAt
      }));

      return {
        success: true,
        message: `Successfully replaced store catalog with ${sanitized.length} products${imagesRestored > 0 ? ` and restored ${imagesRestored} images` : ''}.`,
        mergedProducts: sanitized,
        categories,
        savedCustomTags,
        pagesContent,
        storiesSettings,
        pagesSEO,
        addedCount: sanitized.length,
        updatedCount: 0,
        imagesRestored
      };
    } else {
      // Smart Merge (Recommended)
      const mergeResult = executeSmartMerge(currentProducts, rawProducts);

      return {
        success: true,
        message: `Smart Merge successful: ${mergeResult.addedCount} newly added product(s), ${mergeResult.updatedCount} updated${imagesRestored > 0 ? `, and ${imagesRestored} images unpacked into category folders` : ''}.`,
        mergedProducts: mergeResult.mergedProducts,
        categories,
        savedCustomTags,
        pagesContent,
        storiesSettings,
        pagesSEO,
        addedCount: mergeResult.addedCount,
        updatedCount: mergeResult.updatedCount,
        imagesRestored
      };
    }
  } catch (err: any) {
    return {
      success: false,
      message: `Failed to restore backup: ${err?.message || 'Unknown error'}`,
      mergedProducts: currentProducts,
      addedCount: 0,
      updatedCount: 0,
      imagesRestored: 0
    };
  }
}

/**
 * Creates and downloads a self-contained ZIP backup of all Coveristic Stories
 * along with their image files neatly bundled in images/stories/.
 */
export async function createAndDownloadStoriesZipBackup({
  storiesSettings,
  onProgress
}: {
  storiesSettings: StoriesSettings;
  onProgress?: BackupProgressCallback;
}): Promise<void> {
  const zip = new JSZip();
  const dateStamp = new Date().toISOString().slice(0, 10);
  const rootFolderName = `coveristic-stories-backup-${dateStamp}`;
  const root = zip.folder(rootFolderName) || zip;

  root.folder('images/stories');

  const storyItems = Array.isArray(storiesSettings?.items) ? storiesSettings.items : [];
  const totalStories = storyItems.length;

  onProgress?.({
    stage: 'gathering',
    message: `Preparing backup for ${totalStories} Coveristic stories...`,
    percent: 10,
    current: 0,
    total: totalStories
  });

  const sanitizedStoryItems: StoryItem[] = [];
  const processedImages = new Set<string>();

  for (let sIdx = 0; sIdx < totalStories; sIdx++) {
    const story = storyItems[sIdx];
    const storyCopy: StoryItem = { ...story };
    const storyIdent = story.id || `story_${sIdx + 1}`;

    const percent = Math.min(85, Math.round(15 + (sIdx / (totalStories || 1)) * 70));
    onProgress?.({
      stage: 'downloading_images',
      message: `Archiving Story [${sIdx + 1}/${totalStories}]: "${story.title}"...`,
      percent,
      current: sIdx + 1,
      total: totalStories
    });

    // Archive main story image
    if (story.image && typeof story.image === 'string' && !story.image.startsWith('data:image/svg+xml')) {
      const archivedName = await archiveImageToZip(root, story.image, 'stories', storyIdent, 'cover', processedImages);
      if (archivedName) {
        storyCopy.image = `/images/stories/${archivedName}`;
      }
    }

    // Archive all slide images in this story
    if (Array.isArray(story.images) && story.images.length > 0) {
      const archivedSlideUrls: string[] = [];
      for (let imgIdx = 0; imgIdx < story.images.length; imgIdx++) {
        const slideUrl = story.images[imgIdx];
        if (slideUrl && typeof slideUrl === 'string' && !slideUrl.startsWith('data:image/svg+xml')) {
          const archivedName = await archiveImageToZip(root, slideUrl, 'stories', storyIdent, `slide_${imgIdx + 1}`, processedImages);
          if (archivedName) {
            archivedSlideUrls.push(`/images/stories/${archivedName}`);
          } else {
            archivedSlideUrls.push(slideUrl);
          }
        } else {
          archivedSlideUrls.push(slideUrl);
        }
      }
      storyCopy.images = archivedSlideUrls;
    }

    sanitizedStoryItems.push(storyCopy);
  }

  const sanitizedStories: StoriesSettings = {
    ...storiesSettings,
    items: sanitizedStoryItems
  };

  const manifest = {
    version: '2.0',
    type: 'coveristic-stories-backup',
    timestamp: new Date().toISOString(),
    exportedAt: new Date().toLocaleString(),
    storyCount: sanitizedStoryItems.length,
    totalImages: processedImages.size,
    storiesSettings: sanitizedStories
  };

  root.file('manifest.json', JSON.stringify(manifest, null, 2));
  root.file('stories.json', JSON.stringify(sanitizedStories, null, 2));

  onProgress?.({
    stage: 'compressing',
    message: 'Compressing stories package with image lookbooks...',
    percent: 90,
    current: totalStories,
    total: totalStories
  });

  const zipBlob = await zip.generateAsync(
    {
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    },
    (meta) => {
      onProgress?.({
        stage: 'compressing',
        message: `Compressing package: ${Math.round(meta.percent)}%`,
        percent: 90 + Math.round((meta.percent / 100) * 8),
        current: totalStories,
        total: totalStories
      });
    }
  );

  const downloadFilename = `coveristic-stories-backup-${dateStamp}.zip`;
  const downloadUrl = URL.createObjectURL(zipBlob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = downloadFilename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(downloadUrl);

  onProgress?.({
    stage: 'complete',
    message: `Stories backup complete! Downloaded ${downloadFilename}`,
    percent: 100,
    current: totalStories,
    total: totalStories
  });
}

/**
 * Exports stories settings as a lightweight .json backup file
 */
export function exportStoriesJsonBackup(storiesSettings: StoriesSettings): void {
  const jsonStr = JSON.stringify(storiesSettings, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const dateStamp = new Date().toISOString().slice(0, 10);
  const downloadFilename = `coveristic-stories-backup-${dateStamp}.json`;

  const downloadUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = downloadFilename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(downloadUrl);
}

/**
 * Restores or parses Coveristic Stories from either a .zip backup or a raw .json backup
 */
export async function parseAndRestoreStoriesBackup({
  file,
  currentStories,
  mode = 'merge',
  onProgress
}: {
  file: File;
  currentStories: StoriesSettings;
  mode?: 'merge' | 'replace';
  onProgress?: BackupProgressCallback;
}): Promise<{
  success: boolean;
  message: string;
  storiesSettings: StoriesSettings;
  addedCount: number;
  updatedCount: number;
  imagesRestored: number;
}> {
  const isZip = file.name.endsWith('.zip') || file.type.includes('zip') || file.type.includes('octet-stream');

  try {
    let incomingStories: StoriesSettings | null = null;
    let imagesRestored = 0;

    if (isZip) {
      onProgress?.({
        stage: 'gathering',
        message: 'Reading stories ZIP archive...',
        percent: 10,
        current: 0,
        total: 100
      });

      const zip = await JSZip.loadAsync(file);

      // Locate stories.json or manifest.json
      const storiesJsonFile = zip.file('stories.json') || Object.values(zip.files).find(f => f.name.endsWith('stories.json') && !f.dir);
      if (storiesJsonFile) {
        const content = await storiesJsonFile.async('text');
        incomingStories = JSON.parse(content);
      } else {
        const manifestFile = zip.file('manifest.json') || Object.values(zip.files).find(f => f.name.endsWith('manifest.json') && !f.dir);
        if (manifestFile) {
          const content = await manifestFile.async('text');
          const parsed = JSON.parse(content);
          if (parsed.storiesSettings) {
            incomingStories = parsed.storiesSettings;
          }
        }
      }

      // Restore images to server /api/upload.php
      const imageEntries = Object.entries(zip.files).filter(([name, zipEntry]) => {
        return !zipEntry.dir && (
          name.includes('images/') ||
          name.endsWith('.webp') ||
          name.endsWith('.jpg') ||
          name.endsWith('.png') ||
          name.endsWith('.jpeg')
        );
      });

      const totalImages = imageEntries.length;
      if (totalImages > 0) {
        for (let i = 0; i < totalImages; i++) {
          const [filePath, zipEntry] = imageEntries[i];
          const pathParts = filePath.split('/');
          const filename = pathParts.pop() || `story_img_${Date.now()}.webp`;

          onProgress?.({
            stage: 'restoring_images',
            message: `Restoring story image [${i + 1}/${totalImages}]: ${filename}...`,
            percent: 20 + Math.round(((i + 1) / totalImages) * 60),
            current: i + 1,
            total: totalImages
          });

          try {
            const base64Data = await zipEntry.async('base64');
            const mime = filename.endsWith('.png') ? 'image/png' : filename.endsWith('.jpg') || filename.endsWith('.jpeg') ? 'image/jpeg' : 'image/webp';
            const dataUrl = `data:${mime};base64,${base64Data}`;

            await fetch('/api/upload.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                category: 'stories',
                catalog: 'stories',
                filename,
                image: dataUrl
              })
            }).catch(() => null);

            imagesRestored++;
          } catch {
            // continue gracefully
          }
        }
      }
    } else {
      // JSON File
      onProgress?.({
        stage: 'gathering',
        message: 'Reading stories JSON file...',
        percent: 20,
        current: 0,
        total: 100
      });

      const text = await file.text();
      const parsed = JSON.parse(text);

      if (parsed && Array.isArray(parsed.items)) {
        incomingStories = parsed;
      } else if (parsed && parsed.storiesSettings && Array.isArray(parsed.storiesSettings.items)) {
        incomingStories = parsed.storiesSettings;
      } else if (Array.isArray(parsed)) {
        incomingStories = {
          ...currentStories,
          items: parsed
        };
      }
    }

    if (!incomingStories || !Array.isArray(incomingStories.items)) {
      return {
        success: false,
        message: 'Invalid stories backup file: could not find stories list',
        storiesSettings: currentStories,
        addedCount: 0,
        updatedCount: 0,
        imagesRestored: 0
      };
    }

    onProgress?.({
      stage: 'merging',
      message: mode === 'merge' ? 'Smart merging stories lookbooks...' : 'Replacing stories list...',
      percent: 90,
      current: incomingStories.items.length,
      total: incomingStories.items.length
    });

    if (mode === 'replace') {
      const finalSettings: StoriesSettings = {
        enabled: incomingStories.enabled !== undefined ? incomingStories.enabled : currentStories.enabled,
        viewDuration: incomingStories.viewDuration || currentStories.viewDuration,
        label: incomingStories.label || currentStories.label,
        items: incomingStories.items
      };

      return {
        success: true,
        message: `Successfully replaced Coveristic Stories with ${finalSettings.items.length} stories${imagesRestored > 0 ? ` and restored ${imagesRestored} images` : ''}.`,
        storiesSettings: finalSettings,
        addedCount: finalSettings.items.length,
        updatedCount: 0,
        imagesRestored
      };
    } else {
      // Smart Merge Stories
      const currentItems = [...(currentStories.items || [])];
      let addedCount = 0;
      let updatedCount = 0;

      for (const inc of incomingStories.items) {
        const matchIdx = currentItems.findIndex(
          c => (c.id && inc.id && c.id === inc.id) || 
               (c.title && inc.title && c.title.toLowerCase().trim() === inc.title.toLowerCase().trim())
        );

        if (matchIdx >= 0) {
          // Merge slides and update fields
          const existing = currentItems[matchIdx];
          const combinedImages = Array.from(new Set([
            ...(existing.images || (existing.image ? [existing.image] : [])),
            ...(inc.images || (inc.image ? [inc.image] : []))
          ])).filter(Boolean);

          currentItems[matchIdx] = {
            ...existing,
            ...inc,
            id: existing.id || inc.id,
            image: inc.image || existing.image || combinedImages[0] || '',
            images: combinedImages.length > 0 ? combinedImages : (inc.images || existing.images),
            badge: inc.badge || existing.badge,
            ctaText: inc.ctaText || existing.ctaText,
            ctaCategory: inc.ctaCategory !== undefined ? inc.ctaCategory : existing.ctaCategory,
            ctaUrl: inc.ctaUrl !== undefined ? inc.ctaUrl : existing.ctaUrl,
            buttonEnabled: inc.buttonEnabled !== undefined ? inc.buttonEnabled : existing.buttonEnabled
          };
          updatedCount++;
        } else {
          // New story
          currentItems.push({
            ...inc,
            id: inc.id || `story-${Date.now()}-${addedCount}`
          });
          addedCount++;
        }
      }

      const finalSettings: StoriesSettings = {
        enabled: incomingStories.enabled !== undefined ? incomingStories.enabled : currentStories.enabled,
        viewDuration: incomingStories.viewDuration || currentStories.viewDuration,
        label: incomingStories.label || currentStories.label,
        items: currentItems
      };

      return {
        success: true,
        message: `Smart Merge completed: ${addedCount} new story added, ${updatedCount} updated${imagesRestored > 0 ? `, and ${imagesRestored} images unpacked into /images/stories/` : ''}.`,
        storiesSettings: finalSettings,
        addedCount,
        updatedCount,
        imagesRestored
      };
    }
  } catch (err: any) {
    return {
      success: false,
      message: `Failed to restore stories: ${err?.message || 'Unknown error'}`,
      storiesSettings: currentStories,
      addedCount: 0,
      updatedCount: 0,
      imagesRestored: 0
    };
  }
}

